/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls.
 * 
 * @validates Requirements 1.1, 3.1, 3.2 - Registration and Login
 */

import api from './api';
import { User, ApiResponse } from '@budgira/shared';

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Storage type key to track which storage is being used
const AUTH_STORAGE_TYPE_KEY = 'budgira_auth_type';

/**
 * Get the appropriate storage based on remember me preference
 */
function getStorage(): Storage {
  const storageType = localStorage.getItem(AUTH_STORAGE_TYPE_KEY);
  return storageType === 'local' ? localStorage : sessionStorage;
}

/**
 * Save tokens to the appropriate storage
 */
function saveTokens(accessToken: string, refreshToken?: string, rememberMe?: boolean): void {
  // Clear both storages first to avoid conflicts
  localStorage.removeItem('budgira_token');
  localStorage.removeItem('budgira_refresh_token');
  sessionStorage.removeItem('budgira_token');
  sessionStorage.removeItem('budgira_refresh_token');

  const storage = rememberMe ? localStorage : sessionStorage;
  
  // Track which storage type is being used
  if (rememberMe !== undefined) {
    localStorage.setItem(AUTH_STORAGE_TYPE_KEY, rememberMe ? 'local' : 'session');
  }

  storage.setItem('budgira_token', accessToken);
  if (refreshToken) {
    storage.setItem('budgira_refresh_token', refreshToken);
  }
}

/**
 * Get token from the appropriate storage (checks both)
 */
export function getToken(): string | null {
  // First check localStorage (remember me)
  let token = localStorage.getItem('budgira_token');
  if (token) return token;
  
  // Then check sessionStorage
  token = sessionStorage.getItem('budgira_token');
  return token;
}

/**
 * Get refresh token from the appropriate storage (checks both)
 */
export function getRefreshToken(): string | null {
  let token = localStorage.getItem('budgira_refresh_token');
  if (token) return token;
  
  token = sessionStorage.getItem('budgira_refresh_token');
  return token;
}

/**
 * Clear all auth tokens from both storages
 */
export function clearTokens(): void {
  localStorage.removeItem('budgira_token');
  localStorage.removeItem('budgira_refresh_token');
  localStorage.removeItem(AUTH_STORAGE_TYPE_KEY);
  sessionStorage.removeItem('budgira_token');
  sessionStorage.removeItem('budgira_refresh_token');
}

export interface RegisterRequest {
  email: string;
  password: string;
  language?: 'fr' | 'en';
}

export interface LoginResponse {
  user: User;
  accessToken?: string;
  refreshToken?: string;
  requires2FA?: boolean;
  requiresTwoFactor?: boolean;
  requiresEmail2FA?: boolean;
  requiresWorkspaceSelection?: boolean;
  workspaces?: Array<{
    id: string;
    name: string;
    type: string;
    role: string;
    isDefault: boolean;
    ownerEmail?: string;
  }>;
  userId?: string;
  sessionId?: string;
  message?: string;
}

export interface SelectWorkspaceResponse {
  workspace: {
    id: string;
    name: string;
    type: string;
    role: string;
  };
  sharedAccess: {
    canManageCategories: boolean;
    canManagePaymentMethods: boolean;
    accountPermissions: Array<{ accountId: string; permission: 'none' | 'read' | 'write' }>;
    ownerEmail?: string;
    effectiveRole?: 'free' | 'premium' | 'admin';
  } | null;
  message: string;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qrCode: string;
}

export const authService = {
  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<ApiResponse<User>> {
    const response = await api.post<ApiResponse<User>>('/auth/register', data);
    return response.data;
  },

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/login', data);
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      if (accessToken) {
        saveTokens(accessToken, refreshToken, data.rememberMe);
      }
    }
    return response.data;
  },

  /**
   * Verify 2FA code
   */
  async verify2FA(code: string, userId: string, rememberMe?: boolean): Promise<ApiResponse<LoginResponse>> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/verify-2fa', {
      code,
      userId,
    });
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      if (accessToken) {
        saveTokens(accessToken, refreshToken, rememberMe);
      }
    }
    return response.data;
  },

  /**
   * Verify email-based 2FA code
   */
  async verifyEmail2FA(userId: string, sessionId: string, code: string, rememberMe?: boolean): Promise<ApiResponse<LoginResponse>> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/2fa/verify', {
      userId,
      sessionId,
      code,
    });
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      if (accessToken) {
        saveTokens(accessToken, refreshToken, rememberMe);
      }
    }
    return response.data;
  },

  /**
   * Resend email-based 2FA code
   */
  async resendEmail2FA(userId: string, sessionId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/2fa/resend', {
      userId,
      sessionId,
    });
    return response.data;
  },

  /**
   * Select workspace after login (when user has multiple workspaces)
   */
  async selectWorkspace(workspaceId: string): Promise<ApiResponse<SelectWorkspaceResponse>> {
    const response = await api.post<ApiResponse<SelectWorkspaceResponse>>('/auth/select-workspace', {
      workspaceId,
    });
    return response.data;
  },

  /**
   * Setup 2FA
   */
  async setup2FA(): Promise<ApiResponse<TwoFactorSetupResponse>> {
    const response = await api.post<ApiResponse<TwoFactorSetupResponse>>('/auth/setup-2fa');
    return response.data;
  },

  /**
   * Confirm 2FA setup
   */
  async confirm2FA(code: string): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>('/auth/confirm-2fa', { code });
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      clearTokens();
    }
  },

  /**
   * Refresh token
   */
  async refresh(): Promise<ApiResponse<LoginResponse>> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/refresh');
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data;
      if (accessToken) {
        // Keep using the same storage type that was previously set
        const storage = getStorage();
        storage.setItem('budgira_token', accessToken);
        if (refreshToken) {
          storage.setItem('budgira_refresh_token', refreshToken);
        }
      }
    }
    return response.data;
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await api.get<ApiResponse<User>>('/auth/me');
    return response.data;
  },

  /**
   * Verify email
   */
  async verifyEmail(token: string): Promise<ApiResponse<null>> {
    const response = await api.get<ApiResponse<null>>(`/auth/verify-email/${token}`);
    return response.data;
  },

  /**
   * Request password reset - sends reset link to email
   */
  async requestPasswordReset(email: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/password/reset-request', { email });
    return response.data;
  },

  /**
   * Reset password using token from email
   */
  async resetPassword(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post<ApiResponse<{ message: string }>>('/auth/password/reset', { token, password });
    return response.data;
  },

  /**
   * Request email change - sends confirmation link to new email
   */
  async requestEmailChange(newEmail: string, currentPassword: string): Promise<ApiResponse<{ message: string; newEmail: string }>> {
    const response = await api.post<ApiResponse<{ message: string; newEmail: string }>>('/auth/email/change-request', { 
      newEmail, 
      currentPassword 
    });
    return response.data;
  },

  /**
   * Confirm email change using token from email
   */
  async confirmEmailChange(token: string): Promise<ApiResponse<{ message: string; newEmail: string }>> {
    const response = await api.post<ApiResponse<{ message: string; newEmail: string }>>('/auth/email/confirm-change', { token });
    return response.data;
  },

  /**
   * Get pending email change request
   */
  async getPendingEmailChange(): Promise<ApiResponse<{ pending: { newEmail: string; expiresAt: string } | null }>> {
    const response = await api.get<ApiResponse<{ pending: { newEmail: string; expiresAt: string } | null }>>('/auth/email/pending');
    return response.data;
  },

  /**
   * Cancel pending email change request
   */
  async cancelEmailChange(): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete<ApiResponse<{ message: string }>>('/auth/email/cancel-change');
    return response.data;
  },

  /**
   * Get Google Auth status (enabled/disabled)
   */
  async getGoogleAuthStatus(): Promise<{ enabled: boolean }> {
    try {
      const response = await api.get<{ enabled: boolean }>('/auth/google/status');
      return response.data;
    } catch {
      return { enabled: false };
    }
  },
};


export default authService;
